/* eslint-disable @next/next/no-img-element */
import React, { MouseEvent, useEffect, useState, useCallback } from 'react'
import HomeIcon from '@mui/icons-material/Home'
import {
  NewLineArrowIcon,
  SidebarIcon,
  SignOutIcon,
  SupportIcon,
  DashboardIcon,
  NotifictionIcon,
  MyQuestionIcon,
  SidebarResearchIcon
} from '@/src/assets/icons'
import { Box, Drawer, Menu, MenuItem, Skeleton, Typography, Button, useMediaQuery } from '@mui/material'
import { useRouter } from 'next/router'
import CommonApi from '@/pages/api'
import { Avatar } from '../AvatarIcon'
import { useStore } from '@/src/types/store'
import { CurrentStats } from '@/pages/api/endpoints/MarketData'
import Link from 'next/link'
import { useAnnouncementStore } from '@/src/types/announcement'
import { useHeaderContentStore } from '@/src/types/headerContent'
import { formatPrice } from '@/src/types/helper'

export interface Tab {
  id: string | number
  read?: boolean | undefined
  write?: boolean | undefined
  name: string
  title: string
  href: string
  key?: string
  icon?: React.ReactNode
  shortLabel?: string
  alwaysShow?: boolean
  label?: string
}

interface NavItem {
  label: string
  shortLabel?: string
  href: string
  active: boolean
  icon?: React.ReactNode
}

const Header = () => {
  const { TextColorType } = useStore()
  const [showDrawer, setShowDrawer] = useState(false)
  const [showCorporate, setShowCorporate] = useState(false)
  const [showNewsMenu, setShowNewsMenu] = useState(true)
  const isCompact = useMediaQuery('(max-width:1380px)', { noSsr: true })

  const {
    profile,
    clearProfile,
    themeLoading,
    statsData,
    themeLogo,
    homeTheme,
    setStatsData,
    setStatsLoading
  } = useStore()

  const { HeaderCategories } = useAnnouncementStore()

  // The logo and home button open the company website. `home_link_behaviour`
  // (Company & Theme → Link behaviour) decides whether that happens in a new
  // tab or this one; anything other than 'same_tab' — including an older
  // /theme response that omits the field — keeps the new-tab default.
  const openHomeLink = () => {
    const target = homeTheme?.home_link_behaviour === 'same_tab' ? '_self' : '_blank'
    window.open(`https://${homeTheme.primary_domain}`, target)
  }

  const router = useRouter()

  const { headerItems } = useHeaderContentStore()

  const CurrTab = router?.pathname
  const categoryMenu = [
    {
      label: 'Announcements',
      href: '/announcements',
      key: 'announcement',
      icon: (
        <MyQuestionIcon
          className={`fill-white  h-6 w-6 cursor-pointer
              ${CurrTab === '/announcements' ? 'fill-primary-home' : 'fill-icon-darkGray'}`}
        />
      )
    },
    ...(showNewsMenu
      ? [
          {
            label: 'News & Insights',
            shortLabel: 'News',
            href: '/news-insights',
            key: 'update',
            icon: <NotifictionIcon className={`  h-6 w-6 cursor-pointer fill-[#fafafa] `} />
          }
        ]
      : []),
    {
      label: 'Research',
      href: '/research',
      key: 'research',
      icon: <SidebarResearchIcon className={`fill-[#fafafa] h-6 w-6 cursor-pointer`} />
    },
    ...(showCorporate ? [{
      label: 'Corporate',
      href: '/corporate',
      key: 'corporate',
      alwaysShow: true,
      icon: <SidebarResearchIcon className={`fill-[#fafafa] h-6 w-6 cursor-pointer`} />
    }] : [])
  ]

  // /homepage-preview and /corporate?preview=true are admin previews: their nav
  // only switches between the two previews, never to the public pages.
  const isCorporatePreview = router.pathname === '/corporate' && router.query.preview === 'true'
  const isPreview = router.pathname === '/homepage-preview' || isCorporatePreview

  const navItems: NavItem[] = isPreview
    ? [
        {
          label: 'Investor Dashboard Preview',
          shortLabel: 'Dashboard Preview',
          href: '/homepage-preview',
          active: router.pathname === '/homepage-preview',
          icon: <DashboardIcon className="h-6 w-6 fill-[#fafafa]" />
        },
        {
          label: 'Corporate Preview',
          href: '/corporate?preview=true',
          active: isCorporatePreview,
          icon: <SidebarResearchIcon className="fill-[#fafafa] h-6 w-6 cursor-pointer" />
        }
      ]
    : [
        {
          label: 'Investor Dashboard',
          shortLabel: 'Dashboard',
          href: '/',
          active: router.pathname === '/',
          icon: <DashboardIcon className="h-6 w-6 fill-[#fafafa]" />
        },
        ...categoryMenu
          .filter(cat => cat.alwaysShow || HeaderCategories[cat.key] > 0)
          .map(cat => ({
            ...cat,
            active: router.pathname === cat.href && Object.keys(router.query).length === 0
          }))
      ]

  const fetchCurrentStats = useCallback(async () => {
    setStatsLoading(true)
    if (statsData?.current_price) {
      return
    }
    try {
      const resp = await CommonApi({
        path: CurrentStats?.get({
          interval: 'month'
        }),
        method: 'get'
      })
      if (resp.status) {
        setStatsData(resp.data)
      }
    } catch (err) {
      console.log('err', err)
    }
    setStatsLoading(false)
  }, [setStatsData, setStatsLoading, statsData?.current_price])

  useEffect(() => {
    fetchCurrentStats()
  }, [fetchCurrentStats])

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const resp = await CommonApi({
          path: 'show-homepage-sections',
          method: 'get'
        })
        if (resp?.status && Array.isArray(resp.data)) {
          const corpSection = resp.data.find(
            (s: any) => s.title === 'Corporate Governance'
          )
          setShowCorporate(corpSection?.show_section === true)
          const newsSection = resp.data.find((s: any) => s.title === 'In the news')
          setShowNewsMenu(newsSection?.show_news_menu !== false)
        }
      } catch (err) {
        console.error('Failed to fetch sections for nav:', err)
      }
    }
    fetchSections()
  }, [])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleLogout = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const LogoutUser = () => {
    setAnchorEl(null)
    clearProfile()
    router.push({
      pathname: '/login',
      query: {
        user_type: 'user'
      }
    })
  }

  const handleprofile = () => {
    if (profile?.user_type === 'investor') {
      router.push('/my-profile')
    } else {
      router.push('/admin')
    }
    handleClose()
  }

  const toggleDrawer = () => {
    setShowDrawer(true)
  }

  return (
    <div className={`fixed top-0 left-0 w-full z-50 p-2 sm:p-4 pb-0 bg-[#f5f5f8]`}>
      <div
        className={`flex justify-between items-center h-[5.375rem] px-6 bg-primary-home rounded-[20px] transition-transform`}
      >
        <div className="flex gap-2 items-center">
          <div className="block lg:hidden" onClick={toggleDrawer}>
            <SidebarIcon
              className={`w-4 h-3.5 ${TextColorType?.primaryTextDark ? 'fill-white' : 'fill-black'}`}
            />
          </div>
          <div className="hidden md:block">
            {themeLoading ? (
              <Skeleton className="h-10 w-[7rem]" />
            ) : TextColorType?.primaryTextDark ? (
              <img
                onClick={openHomeLink}
                src={themeLogo?.dark_logo}
                alt="Logo"
                className="h-10 w-auto max-w-[12rem] object-contain fill-none cursor-pointer hover:scale-110"
              />
            ) : (
              <img
                onClick={openHomeLink}
                src={themeLogo?.logo}
                alt="Logo"
                className="h-10 w-auto max-w-[12rem] object-contain fill-none cursor-pointer hover:scale-110"
              />
            )}
          </div>
        </div>
        <div
          className={`text-xxs hidden lg:flex rounded-full h-[2.75rem] items-center ${TextColorType?.secondaryTextDark ? 'bg-secondary-home text-white' : 'bg-secondary-home text-black'} font-semibold`}
        >
          <HomeIcon
            className="mx-3 text-[30px] hover:scale-105 text-white self-center"
            onClick={openHomeLink}
          />
          {navItems.map(tab => (
            <button
              key={tab.href}
              type="button"
              onClick={() => router.push(tab.href)}
              className={`cursor-pointer text-[16px] py-3 px-5 ${
                tab.active
                  ? `${TextColorType?.primaryTextDark ? 'text-primary-home' : 'text-black'} bg-white rounded-full`
                  : 'hover:scale-105'
              }`}
              suppressHydrationWarning
            >
              {isCompact && tab.shortLabel ? tab.shortLabel : tab.label}
            </button>
          ))}
        </div>
        <div className="flex  justify-right items-center gap-2 sm:gap-4">
          <div>
            {statsData?.current_price !== 0 && (
              <>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Typography
                    variant="subtitle2"
                    className="text-[20px]"
                    color={`${TextColorType?.primaryTextDark ? '#fff' : '#000'}`}
                  >
                    ASX:{homeTheme?.code}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    className="text-[20px]"
                    color={`${TextColorType?.primaryTextDark ? '#fff' : '#000'}`}
                  >
                    ${formatPrice(statsData?.current_price) || <Skeleton />}
                  </Typography>
                  <Box className="flex items-center gap-1 bg-white py-0.2 px-1.5 rounded-[50px]">
                    {statsData?.percentage !== 0 && (
                      <NewLineArrowIcon
                        className={`w-[14px] h-[7px] ${
                          statsData?.percentage > 0
                            ? 'fill-[#2E7D32] '
                            : 'fill-[#D32F2F] rotate-180'
                        } `}
                      />
                    )}
                    <Typography
                      variant="subtitle2"
                      className={`truncate ${statsData?.percentage === 0 ? 'text-black' : ''}`}
                      color={
                        statsData?.percentage === 0
                          ? '#000000'
                          : statsData?.percentage > 0
                            ? 'success.dark'
                            : 'error.dark'
                      }
                    >
                      {statsData?.percentage?.toFixed(2) || '00'}%
                    </Typography>
                  </Box>
                </div>
              </>
            )}
          </div>
          {profile?.user_type == '' ? (
            <Button
              variant="contained"
              size="large"
              onClick={() => router.push('/login')}
              className="bg-secondary-home rounded-full !px-2 sm:!px-4"
            >
              <Typography
                variant="buttonLarge_md"
                className={`leading-[1.75rem] normal-case`}
                color={TextColorType?.secondaryTextDark ? '#ffffff' : '#000000'}
              >
                Join
              </Typography>
            </Button>
          ) : (
            <button data-testid="automation-IRHQ_Header-Avatar" onClick={handleLogout}>
              <Avatar profile={profile} />
            </button>
          )}

          <Menu
            className="mt-6 w-full"
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button'
            }}
            classes={{
              paper: 'rounded-[10px] min-w-[220px]'
            }}
          >
            <div className="p-0 py-2 gap-2 flex flex-col">
              <div className="flex flex-col gap-1 justify-start px-4 py-2">
                <div className="flex gap-2">
                  <div className="flex items-top">
                    <Avatar
                      profile={profile}
                      data-testid="automation-IRHQ_HeaderMenu-Avatar"
                      className={`w-10 h-10 bg-primary-home ${TextColorType?.primaryTextDark ? 'text-white' : 'text-black'} rounded-full flex justify-center items-center`}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography variant="subtitle1" color="text.primary">
                      {' '}
                      {profile?.first_name || 'user'} {profile?.last_name || ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {' '}
                      {profile?.email || 'josn@mail.com'}
                    </Typography>
                    <div
                      className={`cursor-pointer text-x font-medium ${TextColorType?.primaryTextDark ? 'text-primary-home' : 'text-black'}`}
                      onClick={handleprofile}
                      data-testid="automation-IRHQ_HeaderMenu-ManageProfile"
                    >
                      <Typography variant="buttonLink">
                        {profile?.user_type === 'investor' ? 'Manage Profile' : 'Admin portal'}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <hr />
              </div>
              <div className="flex flex-col ">
                <Link
                  href={`mailto:${profile?.service_email ? profile?.service_email : 'support@relait.com.au'}`}
                  target="_blank"
                >
                  <MenuItem
                    data-testid="automation-IRHQ_HeaderMenu-Profile"
                    className="flex gap-2 py-[6px] px-4"
                    onClick={() => router.push('/support')}
                  >
                    <div>
                      <SupportIcon className="w-6 h-6 fill-none" />
                    </div>
                    <Typography variant="menuItemDefault">Support</Typography>
                  </MenuItem>
                </Link>
                <MenuItem
                  onClick={LogoutUser}
                  data-testid="automation-IRHQ_HeaderMenu-Signout"
                  className="flex gap-2 py-[6px] px-4"
                >
                  <div>
                    <SignOutIcon className="w-6 h-6 fill-none" />
                  </div>
                  <Typography variant="menuItemDefault">Sign out</Typography>
                </MenuItem>
              </div>
            </div>
          </Menu>
          <Drawer
            open={showDrawer}
            className="flex flex-col gap-4"
            onClose={() => setShowDrawer(false)}
          >
            <div className="bg-primary-home text-white min-h-screen flex flex-col gap-4">
              <div className="flex justify-center mt-6">
                {TextColorType?.primaryTextDark ? (
                  <img
                    onClick={openHomeLink}
                    src={themeLogo.dark_logo}
                    alt="Logo"
                    className="h-10 w-auto max-w-[12rem] object-contain fill-none hover:scale-110 cursor-pointer"
                  />
                ) : (
                  <img
                    onClick={openHomeLink}
                    src={themeLogo?.logo}
                    alt="Logo"
                    className="h-10 w-auto max-w-[12rem] object-contain fill-none hover:scale-110 cursor-pointer"
                  />
                )}
              </div>
              <Box sx={{ width: 250 }} role="presentation" marginTop={4}>
                {navItems.map(tab => (
                  <button
                    key={tab.href}
                    type="button"
                    onClick={() => router.push(tab.href)}
                    className={`block w-full text-left cursor-pointer text-[16px] py-3 mt-2 px-6 ${TextColorType.primaryTextDark ? 'text-white' : 'text-black'} ${
                      tab.active
                        ? `${TextColorType?.secondaryTextDark ? 'text-white' : 'text-black'} bg-secondary-home`
                        : `hover:bg-secondary-home ${TextColorType?.secondaryTextDark ? 'hover:text-white' : 'hover:text-black'}`
                    }`}
                  >
                    <span className="flex gap-1.5">
                      <span>{tab.icon}</span>
                      <span className="w-44">{tab.label}</span>
                    </span>
                  </button>
                ))}
              </Box>
            </div>
          </Drawer>
        </div>
      </div>
    </div>
  )
}

export default Header
